import { bigIntParser } from "../utils/functions/bigIntParser";
import { endpointGenerator } from "../utils/functions/endpointGenerator";
import { type Execution } from "../utils/functions/execution";
import { paginateEndpoint } from "../utils/functions/paginateEndpoint";
import {
  type Chain,
  type GoldRushResponse,
} from "../utils/types/Generic.types";
import {
  type GetAllTransactionsForAddressQueryParamOpts,
  type GetTimeBucketTransactionsForAddressQueryParamOpts,
  type GetTransactionQueryParamOpts,
  type GetTransactionSummaryQueryParamOpts,
  type GetTransactionsForAddressV3QueryParamOpts,
  type GetTransactionsForBlockQueryParamOpts,
  type RecentTransactionsResponse,
  type TransactionResponse,
  type TransactionsBlockResponse,
  type TransactionsResponse,
  type TransactionsSummaryResponse,
  type TransactionsTimeBucketResponse,
} from "../utils/types/TransactionService.types";

/**
 * Transactions API
 *
 */
export class TransactionService {
  constructor(private execution: Execution) {}

  /**
   *
   * Commonly used to fetch and render a single transaction including its decoded log events.
   *
   * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
   * @param {string} txHash - The transaction hash.
   * @param {GetTransactionQueryParamOpts} queryParamOpts
   *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
   *   - `noLogs`: Omit log events.
   *   - `withInternal`: Include internal transfers/transactions.
   *   - `withState`: Include all transaction state changes with before and after values.
   *   - `withInputData`: Include the transaction's input data such as the Method ID.
   *
   */
  public async getTransaction(
    chainName: Chain,
    txHash: string,
    queryParamOpts?: GetTransactionQueryParamOpts
  ): Promise<GoldRushResponse<TransactionResponse>> {
    const endpoint = endpointGenerator(
      `${chainName}/transaction_v2/${txHash}`,
      [
        {
          key: "quote-currency",
          value: queryParamOpts?.quoteCurrency,
        },
        {
          key: "no-logs",
          value: queryParamOpts?.noLogs,
        },
        {
          key: "with-internal",
          value: queryParamOpts?.withInternal,
        },
        {
          key: "with-state",
          value: queryParamOpts?.withState,
        },
        {
          key: "with-input-data",
          value: queryParamOpts?.withInputData,
        },
      ]
    );

    const parseData = (data: GoldRushResponse<TransactionResponse>) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((txItem) => ({
              ...txItem,
              value: bigIntParser(txItem.value),
              fees_paid: bigIntParser(txItem.fees_paid),
              block_signed_at: txItem.block_signed_at
                ? new Date(txItem.block_signed_at)
                : null,
              log_events: txItem.log_events
                ? txItem.log_events.map((logItem) => ({
                    ...logItem,
                    block_signed_at: logItem.block_signed_at
                      ? new Date(logItem.block_signed_at)
                      : null,
                  }))
                : null,
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<TransactionResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * Commonly used to fetch and render the most recent transactions involving an address. Frequently seen in wallet applications.
   *
   * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
   * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
   * @param {GetAllTransactionsForAddressQueryParamOpts} queryParamOpts
   *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
   *   - `noLogs`: Omit log events.
   *   - `blockSignedAtAsc`: Sort the transactions in ascending chronological order. By default, it's set to `false` and returns transactions in descending chronological order.
   *   - `withInternal`: Include internal transfers/transactions.
   *   - `withState`: Include all transaction state changes with before and after values.
   *   - `withInputData`: Include the transaction's input data such as the Method ID.
   *
   */
  public async *getAllTransactionsForAddress(
    chainName: Chain,
    walletAddress: string,
    queryParamOpts?: GetAllTransactionsForAddressQueryParamOpts
  ): AsyncIterable<GoldRushResponse<RecentTransactionsResponse>> {
    const endpoint = endpointGenerator(
      `${chainName}/address/${walletAddress}/transactions_v3`,
      [
        {
          key: "quote-currency",
          value: queryParamOpts?.quoteCurrency,
        },
        {
          key: "no-logs",
          value: queryParamOpts?.noLogs,
        },
        {
          key: "block-signed-at-asc",
          value: queryParamOpts?.blockSignedAtAsc,
        },
        {
          key: "with-internal",
          value: queryParamOpts?.withInternal,
        },
        {
          key: "with-state",
          value: queryParamOpts?.withState,
        },
        {
          key: "with-input-data",
          value: queryParamOpts?.withInputData,
        },
      ]
    );

    const parseData = (data: GoldRushResponse<RecentTransactionsResponse>) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((txItem) => ({
              ...txItem,
              value: bigIntParser(txItem.value),
              fees_paid: bigIntParser(txItem.fees_paid),
              block_signed_at: txItem.block_signed_at
                ? new Date(txItem.block_signed_at)
                : null,
              log_events: txItem.log_events
                ? txItem.log_events.map((logItem) => ({
                    ...logItem,
                    block_signed_at: logItem.block_signed_at
                      ? new Date(logItem.block_signed_at)
                      : null,
                  }))
                : null,
            }))
          : null;
      }

      return data;
    };

    for await (const data of paginateEndpoint<RecentTransactionsResponse>(
      endpoint,
      this.execution,
      parseData,
      "links"
    )) {
      yield data;
    }
  }

  /**
   *
   * Commonly used to fetch and render the most recent transactions involving an address. Frequently seen in wallet applications.
   *
   * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
   * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
   * @param {GetAllTransactionsForAddressQueryParamOpts} queryParamOpts
   *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
   *   - `noLogs`: Omit log events.
   *   - `blockSignedAtAsc`: Sort the transactions in ascending chronological order. By default, it's set to `false` and returns transactions in descending chronological order.
   *   - `withInternal`: Include internal transfers/transactions.
   *   - `withState`: Include all transaction state changes with before and after values.
   *   - `withInputData`: Include the transaction's input data such as the Method ID.
   *
   */
  public async getAllTransactionsForAddressByPage(
    chainName: Chain,
    walletAddress: string,
    queryParamOpts?: GetAllTransactionsForAddressQueryParamOpts
  ): Promise<GoldRushResponse<RecentTransactionsResponse>> {
    const searchParams = [
      {
        key: "quote-currency",
        value: queryParamOpts?.quoteCurrency,
      },
      {
        key: "no-logs",
        value: queryParamOpts?.noLogs,
      },
      {
        key: "block-signed-at-asc",
        value: queryParamOpts?.blockSignedAtAsc,
      },
      {
        key: "with-internal",
        value: queryParamOpts?.withInternal,
      },
      {
        key: "with-state",
        value: queryParamOpts?.withState,
      },
      {
        key: "with-input-data",
        value: queryParamOpts?.withInputData,
      },
    ];

    const endpoint = endpointGenerator(
      `${chainName}/address/${walletAddress}/transactions_v3`,
      searchParams
    );

    const parseData = (data: GoldRushResponse<RecentTransactionsResponse>) => {
      if (data.data) {
        data.data.prev = data.data?.links?.prev
          ? async () =>
              this.execution.execute<RecentTransactionsResponse>(
                endpointGenerator(data.data?.links?.prev, searchParams),
                parseData
              )
          : null;
        data.data.next = data.data?.links?.next
          ? async () =>
              this.execution.execute<RecentTransactionsResponse>(
                endpointGenerator(data.data?.links?.next, searchParams),
                parseData
              )
          : null;
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((txItem) => ({
              ...txItem,
              value: bigIntParser(txItem.value),
              fees_paid: bigIntParser(txItem.fees_paid),
              block_signed_at: txItem.block_signed_at
                ? new Date(txItem.block_signed_at)
                : null,
              log_events: txItem.log_events
                ? txItem.log_events.map((logItem) => ({
                    ...logItem,
                    block_signed_at: logItem.block_signed_at
                      ? new Date(logItem.block_signed_at)
                      : null,
                  }))
                : null,
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<RecentTransactionsResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * Commonly used to fetch all transactions including their decoded log events in a block and further flag interesting wallets or transactions.
   *
   * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
   * @param {number} blockHeight - The requested block height.
   * @param {GetTransactionsForBlockQueryParamOpts} queryParamOpts
   *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
   *   - `noLogs`: Omit log events.
   *
   */
  public async getTransactionsForBlock(
    chainName: Chain,
    blockHeight: number | string | "latest",
    queryParamOpts?: GetTransactionsForBlockQueryParamOpts
  ): Promise<GoldRushResponse<TransactionsBlockResponse>> {
    const endpoint = endpointGenerator(
      `${chainName}/block/${blockHeight}/transactions_v3`,
      [
        {
          key: "quote-currency",
          value: queryParamOpts?.quoteCurrency,
        },
        {
          key: "no-logs",
          value: queryParamOpts?.noLogs,
        },
      ]
    );

    const parseData = (data: GoldRushResponse<TransactionsBlockResponse>) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((txItem) => ({
              ...txItem,
              value: bigIntParser(txItem.value),
              fees_paid: bigIntParser(txItem.fees_paid),
              block_signed_at: txItem.block_signed_at
                ? new Date(txItem.block_signed_at)
                : null,
              log_events: txItem.log_events
                ? txItem.log_events.map((logItem) => ({
                    ...logItem,
                    block_signed_at: logItem.block_signed_at
                      ? new Date(logItem.block_signed_at)
                      : null,
                  }))
                : null,
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<TransactionsBlockResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * Commonly used to fetch the earliest and latest transactions, and the transaction count for a wallet. Calculate the age of the wallet and the time it has been idle and quickly gain insights into their engagement with web3.
   *
   * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
   * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
   * @param {GetTransactionSummaryQueryParamOpts} queryParamOpts
   *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
   *   - `withGas`: Include gas summary details. Additional charge of 1 credit when true. Response times may be impacted for wallets with millions of transactions.
   *
   */
  public async getTransactionSummary(
    chainName: Chain,
    walletAddress: string,
    queryParamOpts?: GetTransactionSummaryQueryParamOpts
  ): Promise<GoldRushResponse<TransactionsSummaryResponse>> {
    const endpoint = endpointGenerator(
      `${chainName}/address/${walletAddress}/transactions_summary`,
      [
        {
          key: "quote-currency",
          value: queryParamOpts?.quoteCurrency,
        },
        {
          key: "with-gas",
          value: queryParamOpts?.withGas,
        },
      ]
    );

    const parseData = (data: GoldRushResponse<TransactionsSummaryResponse>) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((txsItem) => ({
              ...txsItem,
              earliest_transaction: {
                ...txsItem.earliest_transaction!,
                block_signed_at: txsItem?.earliest_transaction?.block_signed_at
                  ? new Date(txsItem.earliest_transaction.block_signed_at)
                  : null,
              },
              latest_transaction: {
                ...txsItem.latest_transaction!,
                block_signed_at: txsItem?.latest_transaction?.block_signed_at
                  ? new Date(txsItem?.latest_transaction?.block_signed_at)
                  : null,
              },
              // ? API vs docs non-consistent
              // gas_summary: {
              //   ...txsItem.gas_summary,
              //   total_fees_paid: bigIntParser(
              //     txsItem.gas_summary.total_fees_paid
              //   ),
              // },
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<TransactionsSummaryResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
   * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
   * @param {number} page - The requested page, 0-indexed.
   * @param {GetTransactionsForAddressV3QueryParamOpts} queryParamOpts
   *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
   *   - `noLogs`: Omit log events.
   *   - `blockSignedAtAsc`: Sort the transactions in ascending chronological order. By default, it's set to `false` and returns transactions in descending chronological order.
   *
   */
  public async getTransactionsForAddressV3(
    chainName: Chain,
    walletAddress: string,
    page: number,
    queryParamOpts?: GetTransactionsForAddressV3QueryParamOpts
  ): Promise<GoldRushResponse<TransactionsResponse>> {
    const searchParams = [
      {
        key: "quote-currency",
        value: queryParamOpts?.quoteCurrency,
      },
      {
        key: "no-logs",
        value: queryParamOpts?.noLogs,
      },
      {
        key: "block-signed-at-asc",
        value: queryParamOpts?.blockSignedAtAsc,
      },
    ];
    const endpoint = endpointGenerator(
      `${chainName}/address/${walletAddress}/transactions_v3/page/${page}`,
      searchParams
    );

    const parseData = (data: GoldRushResponse<TransactionsResponse>) => {
      if (data.data) {
        data.data.prev = data.data?.links?.prev
          ? async () =>
              this.execution.execute<TransactionsResponse>(
                endpointGenerator(data.data?.links?.prev, searchParams),
                parseData
              )
          : null;
        data.data.next = data.data?.links?.next
          ? async () =>
              this.execution.execute<TransactionsResponse>(
                endpointGenerator(data.data?.links?.next, searchParams),
                parseData
              )
          : null;
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((txItem) => ({
              ...txItem,
              value: bigIntParser(txItem.value),
              fees_paid: bigIntParser(txItem.fees_paid),
              block_signed_at: txItem.block_signed_at
                ? new Date(txItem.block_signed_at)
                : null,
              log_events: txItem.log_events
                ? txItem.log_events.map((logItem) => ({
                    ...logItem,
                    block_signed_at: logItem.block_signed_at
                      ? new Date(logItem.block_signed_at)
                      : null,
                  }))
                : null,
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<TransactionsResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
   * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
   * @param {number} timeBucket - The 0-indexed 15-minute time bucket. E.g. 27 Feb 2023 05:23 GMT = 1677475383 (Unix time). 1677475383/900=1863861 timeBucket.
   * @param {GetTimeBucketTransactionsForAddressQueryParamOpts} queryParamOpts
   *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
   *   - `noLogs`: Omit log events.
   *
   */
  public async getTimeBucketTransactionsForAddress(
    chainName: Chain,
    walletAddress: string,
    timeBucket: number,
    queryParamOpts?: GetTimeBucketTransactionsForAddressQueryParamOpts
  ): Promise<GoldRushResponse<TransactionsTimeBucketResponse>> {
    const searchParams = [
      {
        key: "quote-currency",
        value: queryParamOpts?.quoteCurrency,
      },
      {
        key: "no-logs",
        value: queryParamOpts?.noLogs,
      },
    ];
    const endpoint = endpointGenerator(
      `${chainName}/bulk/transactions/${walletAddress}/${timeBucket}`,
      searchParams
    );

    const parseData = (
      data: GoldRushResponse<TransactionsTimeBucketResponse>
    ) => {
      if (data.data) {
        data.data.prev = data.data?.links?.prev
          ? async () =>
              this.execution.execute<TransactionsTimeBucketResponse>(
                endpointGenerator(data.data?.links?.prev, searchParams),
                parseData
              )
          : null;
        data.data.next = data.data?.links?.next
          ? async () =>
              this.execution.execute<TransactionsTimeBucketResponse>(
                endpointGenerator(data.data?.links?.next, searchParams),
                parseData
              )
          : null;
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((txItem) => ({
              ...txItem,
              value: bigIntParser(txItem.value),
              fees_paid: bigIntParser(txItem.fees_paid),
              block_signed_at: txItem.block_signed_at
                ? new Date(txItem.block_signed_at)
                : null,
              log_events: txItem.log_events
                ? txItem.log_events.map((logItem) => ({
                    ...logItem,
                    block_signed_at: logItem.block_signed_at
                      ? new Date(logItem.block_signed_at)
                      : null,
                  }))
                : null,
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<TransactionsTimeBucketResponse>(
      endpoint,
      parseData
    );
  }
}
